<template>
  <Loading v-if="loading" />
  <div class="callerAuthentication">
    <Title :title="$t('callerAuthentication')" />
    <div v-if="authenticationRequestSent" class="callerAuthentication__tip">
      <p>{{ $t('returnToBrowserTip') }}</p>
    </div>
    <div class="callerAuthentication__authForm">
      <div class="callerAuthentication__title">
        <p class="authentication">
          {{ $t('authentication') }}
        </p>
      </div>
      <FormKit
        id="phoneNumber-form"
        v-model="phoneNumberData"
        type="form"
        :actions="false"
      >
        <div class="phone-input">
          <Input
            class="input-phoneNumber"
            name="phoneNumber"
            type="tel"
            :label="$t('phoneNumber')"
            @input="changeInput"
            @keypress.enter.prevent="handleEnter"
          />
          <Button
            class="callerAuthentication-button"
            variant="bordered"
            :title="$t('search')"
            @click="authenticateSearch"
          />
        </div>
      </FormKit>
      <div class="callerAuthentication-info">
        <FormKit
          id="profile-form"
          v-model="formData"
          type="form"
          :actions="false"
        >
          <div class="callerAuthentication-name">
            <div v-for="(el, index) in authenticationNameInfo" :key="index">
              <Input
                class="input-name"
                :type="el.type"
                :name="el.name"
                :label="$t(el.label)"
                disabled
              />
            </div>
          </div>
          <div class="callerAuthentication-otherInfo">
            <div
              v-for="(el, index) in authenticationSsnAndAddressInfo"
              :key="index"
            >
              <Input
                class="input-otherInfo"
                :type="el.type"
                :name="el.name"
                :label="$t(el.label)"
                disabled
              />
            </div>
          </div>
        </FormKit>
      </div>
    </div>
    <div class="callerAuthentication__button">
      <div class="block">
        <Button
          class="firstButton"
          :variant="formData.phoneNumber ? 'bordered' : 'disabledBackground'"
          :title="$t('send')"
          :disabled="formData.phoneNumber ? false : true"
          @click="sendSMS"
        />
        <Button
          v-if="openButton"
          class="authCheck"
          :variant="getButtonAuthentication()"
          :title="$t(getButtonAuthentication())"
        />
        <Button
          v-if="openButtonUserFound"
          class="authCheck"
          :variant="getButtonUserFound()"
          :title="$t(getButtonUserFound())"
        />
        <Button
          v-if="userNotFound && !isBankId"
          class="lastButton"
          :variant="messageSent ? 'disabled' : 'filled'"
          :title="$t('sendSMS')"
          :disabled="messageSent ? true : false"
          @click="initSendAppSMS"
        />
      </div>
      <div class="block">
        <Button
          v-if="openButtonUserFoundSMS"
          class="longButton"
          variant="filled"
          :title="$t('sendSMSBankID')"
          @click="sendSMSBankID"
        />
        <Button
          v-if="openLoading"
          class="authCheckSMS"
          :variant="getButtonAuthentication()"
          :title="$t(getButtonAuthentication())"
        />
      </div>
    </div>
    <div
      v-if="authenticated === 'done' || authenticated === 'completed'"
      class="callerAuthentication__consentGiven"
    >
      <Checkbox name="consentGiven" :value="consentGiven" />
      <span>
        {{ $t('consentGivenText') }}
      </span>
    </div>
    <PaymentDetails
      v-if="authenticated === 'done' || authenticated === 'completed'"
      :user-id="formData.userId"
      :is-bank-id="isBankId"
      :auth-record-id="authRecordId"
      :real-time-value-sms="realTimeValueSMS"
      :reference="callerAuthenticate?.reference"
      @clear-all-forms="clearAllForms"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';
import { getDatabase, ref, onValue, off } from 'firebase/database';

import Loading from '@/views/Loading.vue';
import PaymentDetails from './PaymentDetails/PaymentDetails.vue';

import { defaultFormData } from './configs';
import {
  authenticationNameInfo,
  authenticationSsnAndAddressInfo
} from '@/constants';

import { InputFileEvent } from '@/types';

@Options({
  name: 'CallerAuthentication',
  props: ['isNewOrder'],
  components: {
    Loading,
    PaymentDetails
  },
  data() {
    return {
      loading: false,
      phoneNumberData: {
        phoneNumber: '+358'
      },
      formData: defaultFormData,
      userNotFound: false,
      messageSent: false,
      consentGiven: true,
      isBankId: false,
      authenticationNameInfo,
      authenticationSsnAndAddressInfo,
      authenticationFailed: false,
      openButton: false,
      openLoading: false,
      openButtonUserFound: false,
      openButtonUserFoundSMS: false,
      realTimeValue: {},
      realTimeValueSMS: {},
      reference: '',
      authRecordId: '',
      authenticated: '',
      authenticatedSMS: '',
      authenticationRequestSent: false
    };
  },
  computed: {
    ...mapState('callerAuthentication', [
      'callerAuthentication',
      'callerAuthenticate',
      'activeCallbackRequest',
      'resultSMSBankIDAuthentication',
      'dataBankID'
    ])
  },
  mounted() {
    if (this.activeCallbackRequest) {
      this.phoneNumberData.phoneNumber =
        this.activeCallbackRequest.userPhoneNumber;
    }
  },
  unmounted() {
    this.clearActiveCallbackRequest();
  },
  methods: {
    ...mapActions('callerAuthentication', [
      'getCallerAuthenticationNumber',
      'getCallerAuthenticate',
      'clearActiveCallbackRequest',
      'sendSMSBankIDAuthentication',
      'getAuthenticationDataBankID',
      'clearCallbackRequest'
    ]),
    ...mapActions('account', ['sendAppSMS']),
    handleEnter(event: InputFileEvent) {
      event.preventDefault();

      this.changeInput(event);
      this.authenticateSearch();
    },
    changeInput(e: InputFileEvent) {
      const { value } = e.target;
      this.phoneNumberData.phoneNumber = value;

      if (!value) {
        this.openButtonUserFound = false;
        this.openButtonUserFoundSMS = false;
      }
    },
    getButtonAuthentication() {
      switch (
        this.realTimeValue.authenticated ||
        this.realTimeValueSMS.authenticated
      ) {
        case 'pending':
          return 'authenticationPending';
        case 'failed':
          return 'authenticationFailed';
        case 'cancelled':
          return 'cancelled';
        default:
          return 'authenticated';
      }
    },
    getButtonUserFound() {
      if (this.formData.firstName === '????????' || this.formData.firstName) {
        return 'userFound';
      } else {
        this.userNotFound = true;
        return 'userNotFound';
      }
    },
    async authenticateSearch() {
      this.formData = defaultFormData;
      this.authenticationRequestSent = false;

      this.loading = true;

      await this.getCallerAuthenticationNumber(
        this.phoneNumberData.phoneNumber
      );

      if (this.callerAuthentication) {
        this.formData = {
          phoneNumber: this.callerAuthentication.phoneNumber,
          firstName: '????????',
          lastName: '????????',
          ssn: '????????',
          homeAddress: '????????'
        };
      }

      this.openButtonUserFound = true;
      this.openButtonUserFoundSMS = true;

      this.loading = false;
    },
    async initAuthenticationDataBankID() {
      this.loading = true;

      this.authRecordId = this.resultSMSBankIDAuthentication.reference
        .split('/')
        .pop();

      await this.getAuthenticationDataBankID(this.authRecordId);

      this.formData = {
        ...this.dataBankID
      };

      this.loading = false;
    },
    fetchAuthenticationStatus() {
      const database = getDatabase();

      const fullUrl = this.callerAuthenticate.reference;
      const url = new URL(fullUrl);
      const baseUrl = url.pathname;

      const dbRef = ref(database, baseUrl);
      this.listener = onValue(dbRef, (snapshot) => {
        this.realTimeValue = snapshot.val();

        this.authenticated = this.realTimeValue.authenticated;

        if (this.realTimeValue.authenticated) {
          this.openButton = true;
        }
      });
    },
    detachListener() {
      if (this.listener) {
        off(this.listener);
        this.listener = null;
      }
    },
    async initSendAppSMS() {
      if (!this.messageSent) {
        this.loading = true;

        const payload = {
          customerPhoneNumber: this.phoneNumberData.phoneNumber
        };

        await this.sendAppSMS(payload);
        this.messageSent = true;

        this.loading = false;
      }
    },
    async sendSMS() {
      if (this.formData.phoneNumber) {
        this.loading = true;
        this.openButtonUserFound = false;
        this.authenticationRequestSent = true;

        await this.getCallerAuthenticate({
          userId: this.callerAuthentication.userId
        });
        this.fetchAuthenticationStatus();

        if (!this.callerAuthenticate) {
          this.authenticationFailed = true;
        } else {
          this.authenticationFailed = false;
          this.isBankId = false;
        }

        this.loading = false;
      }
    },
    async sendSMSBankID() {
      this.loading = true;
      this.authenticationRequestSent = true;

      await this.clearCallbackRequest();
      await this.sendSMSBankIDAuthentication({
        phoneNumber: this.phoneNumberData.phoneNumber
      });

      const database = getDatabase();

      const fullUrl = this.resultSMSBankIDAuthentication.reference;
      const url = new URL(fullUrl);
      const baseUrl = url.pathname;
      const dbRef = ref(database, baseUrl);

      this.listener = onValue(dbRef, (snapshot) => {
        this.realTimeValueSMS = snapshot.val();
        this.authenticatedSMS = this.realTimeValueSMS.authenticated;

        if (this.realTimeValueSMS.authenticated) {
          this.openLoading = true;
        }

        if (!this.resultSMSBankIDAuthentication) {
          this.authenticationFailed = true;
        } else {
          this.authenticationFailed = false;
          this.isBankId = true;
        }
      });

      this.loading = false;
    },
    clearAllForms() {
      this.authenticationRequestSent = false;
      this.authenticated = '';
      this.authenticatedSMS = '';
      this.openButton = false;
      this.openLoading = false;
      this.userNotFound = false;
      this.openButtonUserFound = false;
      this.openButtonUserFoundSMS = false;

      this.$formkit.reset('profile-form');
      this.$formkit.reset('phoneNumber-form');
    }
  },
  watch: {
    authenticated(newVal) {
      if (newVal === 'done' || newVal === 'completed') {
        this.formData = {
          ...this.callerAuthentication,
          homeAddress: this.callerAuthentication?.address
        };
      }
    },
    authenticatedSMS(newVal) {
      if (newVal === 'done' || newVal === 'completed') {
        this.initAuthenticationDataBankID();
        this.authenticated = this.realTimeValueSMS.authenticated;
      }
    },
    beforeUnmount() {
      this.detachListener();
    }
  }
})
export default class CallerAuthentication extends Vue {}
</script>

<style lang="scss">
.callerAuthentication {
  display: flex;
  flex-direction: column;
  position: relative;
  .title {
    padding: 24px 0 0 24px;
  }
  .callerAuthentication__tip {
    position: absolute;
    top: 24px;
    right: 24px;
    z-index: 1;
    max-width: 340px;
    padding: 16px;
    border: 1px solid $yellow;
    border-radius: 8px;
    background: $backgroundPending;
    p {
      font-size: $fourteenSize;
      line-height: 20px;
      color: $main-text;
    }
  }
  .callerAuthentication__authForm {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    padding-top: 40px;
    padding-left: 24px;
    .callerAuthentication__title {
      position: relative;
      width: 100%;
      margin-bottom: 34px;
      .authentication {
        font-family: 'Ubuntu-Medium';
        font-size: $sixteenSize;
        color: $main-text;
      }
      &::after {
        content: '';
        position: absolute;
        top: 9px;
        left: 135px;
        width: 100%;
        height: 1px;
        background: $ligher-grey;
      }
      &::before {
        content: '';
        position: absolute;
        top: 7px;
        left: 130px;
        width: 5px;
        height: 5px;
        border-radius: 8px;
        background: $ligher-grey;
      }
    }
    #phoneNumber-form {
      width: 477px;
      position: relative;
      .input-phoneNumber {
        width: 377px;
        .formkit-outer {
          .formkit-wrapper {
            .formkit-inner {
              border: none;
              .formkit-input {
                width: 377px;
                border-bottom-right-radius: 0;
                border-top-right-radius: 0;
                border-right-color: $white;
              }
            }
          }
        }
      }
      .callerAuthentication-button {
        position: absolute;
        width: 101px;
        height: 48px;
        top: 0;
        right: 0;
        border-bottom-left-radius: 0;
        border-top-left-radius: 0;
      }
    }
    .callerAuthentication-info {
      .callerAuthentication-name {
        display: flex;
        justify-content: space-between;
        width: 477px;
        .input-name {
          width: 230px;
          .formkit-input {
            color: $servicesTitle;
            background-color: $timePicker;
          }
        }
      }
      .callerAuthentication-otherInfo {
        width: 477px;
        .input-otherInfo {
          .formkit-outer {
            .formkit-wrapper {
              .formkit-input {
                width: 477px;
                color: $servicesTitle;
                background-color: $timePicker;
              }
            }
          }
        }
      }
    }
  }
  .callerAuthentication__checkboxes {
    margin-top: 30px;
    .callerAuthentication-checkbox {
      margin-bottom: 14px;
    }
  }
  .callerAuthentication__deliveryMethod {
    .callerAuthentication__deliveryMethod-title {
      font-size: $tenSize;
      font-family: 'Ubuntu-Medium';
      color: $columnMainTitle;
      padding-bottom: 32px;
    }
    .callerAuthentication__deliveryMethod-radio {
      display: flex;
      align-items: center;
    }
  }
  .callerAuthentication__button {
    .block {
      display: flex;
      align-items: center;
      padding-left: 24px;
      .firstButton {
        width: 210px;
        margin-right: 10px;
      }
      .authCheck {
        width: 100%;
        min-width: 151px;
        max-width: 220px;
      }
      .lastButton {
        margin-left: 10px;
        width: 152px;
      }
      .longButton {
        width: 260px;
        margin-top: 24px;
      }
      .authCheckSMS {
        width: 208px;
        margin-top: 24px;
        margin-left: 10px;
      }
    }
  }
  .callerAuthentication__consentGiven {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding-left: 24px;
    padding-top: 24px;
    span {
      margin-left: 12px;
      font-size: $fourteenSize;
      line-height: $fourteenSize;
      font-weight: 500;
      font-family: 'Ubuntu-Medium';
    }
  }
}
</style>
